import { fromJS, List } from 'immutable';
import { FUNNEL, TABLE, SANKEY, COLUMN } from 'reporting-data/constants/chartTypes';
import { isJourneyDrilldownReport } from '../types/journeyReport';
import { FunnelVisualization } from 'reporting-visualizations/visualizations/funnel/funnelRecords';
import { TableVisualization } from 'reporting-visualizations/visualizations/table/tableRecords';
import { FIELDS, STAGE_NAME_PROPERTY } from '../constants/fields';
import { createDrilldownGenerator } from '../enhance/drilldown/generate';
import { renderTooltipInfo, getFunnelTooltipFormatter } from '../utils/renderUtils';
import { getNodeColumnsFromQuery } from '../utils/getNodeColumnsFromQuery';
import { CrossTabVisualization } from 'reporting-visualizations/visualizations/crossTab/crossTabRecords';
import { CROSS_TAB } from 'reporting-visualizations/constants/visualizationTypes';
const hasOptionalStages = report => report.journeyQuery && report.journeyQuery.stages && !report.journeyQuery.stages.every(stage => !stage.optional);
const visualizeHorizontalFunnel = (report, datamap, onDrilldown) => FunnelVisualization({
  type: FUNNEL,
  data: datamap,
  dataset: 'primary',
  filterProperty: FIELDS.TO,
  valueProperty: FIELDS.COUNT,
  horizontalFunnel: true,
  primaryChevronProperty: FIELDS.NEXT_STEP_CONVERSION,
  secondaryChevronProperty: '',
  conversionDataInRow: true,
  style: {
    tooltipFormatter: getFunnelTooltipFormatter({
      report,
      dataset: datamap['primary'],
      filterProperty: FIELDS.TO,
      valueProperty: FIELDS.COUNT
    })
  },
  events: {
    onPointClick: createDrilldownGenerator(report, datamap, onDrilldown)
  },
  disableZoom: true
});
const visualizeVerticalFunnel = (report, datamap, onDrilldown) => FunnelVisualization({
  type: FUNNEL,
  data: datamap,
  dataset: 'primary',
  filterProperty: FIELDS.TO,
  valueProperty: hasOptionalStages(report) ? FIELDS.STAGE_TOTAL : FIELDS.COUNT,
  primaryChevronProperty: hasOptionalStages(report) ? FIELDS.STAGE_TOTAL_CONVERSION : FIELDS.NEXT_STEP_CONVERSION,
  secondaryChevronProperty: hasOptionalStages(report) ? FIELDS.SKIP_STAGE_COUNT : '',
  conversionDataInRow: true,
  events: {
    onPointClick: createDrilldownGenerator(report, datamap, onDrilldown)
  }
});
const visualizeTable = (report, datamap, onDrilldown) => {
  const isReportDrilldown = isJourneyDrilldownReport(report);
  const rows = [FIELDS.FROM, FIELDS.TO];
  const values = [FIELDS.COUNT, FIELDS.AVERAGE_TIME_TO_STEP, FIELDS.NEXT_STEP_CONVERSION];
  if (!isReportDrilldown) {
    const stepsFlatMap = getNodeColumnsFromQuery(report.journeyQuery, datamap).flatMap(column => column.nodes.map(node => node.id));
    return CrossTabVisualization({
      type: CROSS_TAB,
      data: datamap,
      dataset: 'primary',
      rows: rows.map(row => ({
        field: row,
        order: {
          defined: List(stepsFlatMap)
        }
      })),
      values: values.map(value => ({
        field: value
      })),
      events: {
        getColumnOnClick: onDrilldown ? columnField => columnField === FIELDS.COUNT ? createDrilldownGenerator(report, datamap, onDrilldown) : undefined : undefined
      }
    });
  }
  const columnFields = Object.keys(datamap.primary.properties);

  //@ts-expect-error Provides a journeys datamap, which works for table dataset anyway
  return TableVisualization({
    data: datamap,
    columns: columnFields.map(field => ({
      field,
      dataset: 'primary',
      onClick: field === FIELDS.COUNT && !isReportDrilldown ? createDrilldownGenerator(report, datamap, onDrilldown) : undefined
    })),
    footer: 'summary'
  });
};
const visualizeSankey = (report, datamap, onDrilldown) => {
  const visualization = {
    data: datamap,
    type: SANKEY,
    dataset: 'primary',
    layout: {
      columns: getNodeColumnsFromQuery(report.journeyQuery, datamap),
      hideOriginNode: true,
      originField: FIELDS.FROM,
      destinationField: FIELDS.TO,
      columnLabelField: STAGE_NAME_PROPERTY,
      measures: {
        linkMeasure: FIELDS.COUNT,
        destinationNodeTotal: FIELDS.STEP_TOTAL,
        linkTooltipMeasures: [FIELDS.NEXT_STEP_CONVERSION, FIELDS.AVERAGE_TIME_TO_STEP],
        renderCustomTooltipInfo: renderTooltipInfo
      }
    },
    events: {
      onPointClick: createDrilldownGenerator(report, datamap, onDrilldown)
    },
    options: {
      scale: true,
      removeZeroDropoffNodes: true
    }
  };
  return fromJS(visualization);
};
const visualizers = {
  [COLUMN]: visualizeHorizontalFunnel,
  [FUNNEL]: visualizeVerticalFunnel,
  [TABLE]: visualizeTable,
  [SANKEY]: visualizeSankey
};
const visualize = (report, datamap, onDrilldown, options) => {
  const visualizer = visualizers[report.chartType];
  return visualizer(report, datamap, onDrilldown, options);
};
export default visualize;